import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Volume2, VolumeX } from "lucide-react";
import AgentMark from "../digiping/AgentMark";
import { AGENTS } from "../utils/agentConfig";
import { clipHighlightFromCaption, placeCaptionCard } from "./captionPlacement";
import { queryTourTarget } from "./tourTarget";
import { isTourTargetInView, scrollHighlightIntoView } from "./scrollTourTarget";
import { isTourTtsMuted, setTourTtsMuted } from "./TourTts";
import "./TourOverlay.css";

const PAD = 8;

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(Boolean(mq.matches));
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);
  return reduced;
}

function measureRect(el, id, extraPad = PAD) {
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (!rect.width && !rect.height) return null;
  return {
    el,
    id,
    x: Math.max(4, rect.left - extraPad),
    y: Math.max(4, rect.top - extraPad),
    w: Math.min(window.innerWidth - 8, rect.width + extraPad * 2),
    h: Math.min(window.innerHeight - 8, rect.height + extraPad * 2),
  };
}

function measureOne(tourId) {
  if (!tourId || typeof document === "undefined") return null;
  return measureRect(queryTourTarget(tourId), tourId);
}

function menuShellFor(el) {
  if (!el?.closest) return null;
  return el.closest(".ps-sidebar-root") || el.closest("#sidebar") || el.closest(".bottom-nav") || null;
}

function measureTargets(step) {
  const nav = measureOne(step?.navTourId);
  const extras = (Array.isArray(step?.extraTourIds) ? step.extraTourIds : [])
    .map((id) => measureOne(id))
    .filter(Boolean);
  const feature = extras.length
    ? null
    : measureOne(step?.tourId) || measureOne(step?.fallbackTourId);
  const seen = new Set();
  const list = [];
  const navEl =
    nav?.el ||
    extras.find((item) => String(item.id || "").includes("nav"))?.el ||
    (feature && String(feature.id || "").includes("nav") ? feature.el : null);
  const shell = menuShellFor(navEl);
  if (shell) {
    const bar = measureRect(shell, `${nav?.id || feature?.id || extras[0]?.id || "nav"}-shell`, 6);
    if (bar) list.push({ ...bar, kind: "shell" });
  }
  for (const item of [nav, feature, ...extras]) {
    if (!item || seen.has(item.id)) continue;
    seen.add(item.id);
    list.push(item);
  }
  return list;
}

export function TourOverlay({
  step,
  stepIndex,
  stepCount,
  agentId = "pingflo",
  onSkip,
  onNext,
  onDoForMe,
  onChoiceAccept,
  onChoiceDefer,
  liveMessage,
  pagePath,
}) {
  const { t, i18n } = useTranslation();
  const reduced = useReducedMotion();
  const cardRef = useRef(null);
  const [targets, setTargets] = useState([]);
  const [muted, setMuted] = useState(() => isTourTtsMuted());
  const [cardSize, setCardSize] = useState({ w: 300, h: 180 });
  const agent = AGENTS[agentId] || AGENTS.pingflo;
  const vw = typeof window === "undefined" ? 0 : window.innerWidth;
  const vh = typeof window === "undefined" ? 0 : window.innerHeight;

  const scrolledKeyRef = useRef("");
  const lastScrollAtRef = useRef(0);

  useEffect(() => {
    scrolledKeyRef.current = "";
    lastScrollAtRef.current = 0;
  }, [stepIndex, step?.tourId, step?.fallbackTourId, step?.extraTourIds, pagePath]);

  useEffect(() => {
    let cancelled = false;
    const sync = () => {
      if (cancelled) return;
      const next = measureTargets(step);
      setTargets(next);
      const feature =
        next.find((t) => t.kind !== "shell" && t.id === step?.tourId) ||
        next.find((t) => t.kind !== "shell" && !String(t.id || "").includes("nav")) ||
        next.find((t) => t.kind !== "shell") ||
        next[0];
      const key = `${stepIndex}:${step?.tourId || ""}:${feature?.id || ""}`;
      if (!feature?.el) return;
      const firstForStep = scrolledKeyRef.current !== key;
      const outOfView = !isTourTargetInView(feature.el);
      if (!firstForStep && !outOfView) return;
      const now = Date.now();
      // Layout can shift after first scroll (e.g. HomePlayerPath loads) — re-center.
      if (!firstForStep && now - lastScrollAtRef.current < 500) return;
      scrolledKeyRef.current = key;
      lastScrollAtRef.current = now;
      scrollHighlightIntoView(feature.el, {
        fromTop: firstForStep && step?.scroll === "topThenCenter",
        reduced,
      });
    };
    sync();
    const timer = window.setInterval(sync, 240);
    window.addEventListener("resize", sync);
    window.addEventListener("scroll", sync, true);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("resize", sync);
      window.removeEventListener("scroll", sync, true);
    };
  }, [
    step,
    step?.tourId,
    step?.navTourId,
    step?.fallbackTourId,
    step?.extraTourIds,
    step?.scroll,
    stepIndex,
    reduced,
    pagePath,
  ]);

  useEffect(() => {
    if (!step?.requireClick || !targets.length) return undefined;
    const els = targets.filter((t) => t.kind !== "shell").map((t) => t.el).filter(Boolean);
    const onClick = () => onNext?.({ from: "target" });
    els.forEach((el) => el.addEventListener("click", onClick));
    return () => els.forEach((el) => el.removeEventListener("click", onClick));
  }, [step?.requireClick, targets, onNext]);

  const title = step?.title || t(step?.titleKey, step?.titleKey || "");
  const body = step?.body || t(step?.bodyKey, step?.bodyKey || "");
  const pos = placeCaptionCard(targets, {
    vw,
    vh,
    cardW: cardSize.w,
    cardH: cardSize.h,
  });
  const captionBox = {
    left: pos.left,
    top: pos.top,
    right: pos.left + cardSize.w,
    bottom: pos.top + cardSize.h,
  };
  const holes = targets.map((item) =>
    step?.keepFullHighlight ? item : clipHighlightFromCaption(item, captionBox)
  );

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return undefined;
    const sync = () => {
      const next = { w: el.offsetWidth, h: el.offsetHeight };
      setCardSize((prev) => (prev.w === next.w && prev.h === next.h ? prev : next));
    };
    sync();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, [stepIndex, step?.titleKey, step?.bodyKey, i18n.language, title, body]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`pingflo-tour-overlay${reduced ? " pingflo-tour-overlay--reduced" : " pingflo-tour-overlay--pulse"}${
        String(step?.tourId || "").startsWith("admin-nav") ? " pingflo-tour-overlay--menu" : ""
      }`}
      style={{ "--tour-accent": agent.accentColor }}
      role="presentation"
    >
      <svg className="pingflo-tour-overlay__dim" width={vw} height={vh} aria-hidden>
        <defs>
          <mask id="pingflo-tour-mask" maskUnits="userSpaceOnUse">
            <rect x="0" y="0" width={vw} height={vh} fill="white" />
            {holes.map((item) => (
              <rect
                key={`cut-${item.id}`}
                x={item.x}
                y={item.y}
                width={item.w}
                height={item.h}
                rx="12"
                fill="black"
              />
            ))}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width={vw}
          height={vh}
          fill="rgba(11, 15, 25, 0.55)"
          mask="url(#pingflo-tour-mask)"
        />
      </svg>
      {holes.map((item) => (
        <div
          key={item.id}
          className={`pingflo-tour-overlay__hole${
            item.kind === "shell"
              ? " pingflo-tour-overlay__hole--shell"
              : String(item.id).includes("nav")
                ? " pingflo-tour-overlay__hole--nav"
                : ""
          }`}
          style={{
            top: item.y,
            left: item.x,
            width: item.w,
            height: item.h,
          }}
        />
      ))}
      <div
        ref={cardRef}
        className="pingflo-tour-card"
        style={{ top: pos.top, left: pos.left }}
        role="dialog"
        aria-modal="false"
        aria-labelledby="pingflo-tour-title"
      >
        <div className="pingflo-tour-card__row">
          <AgentMark agentId={agent.id} size={40} showGlow title={agent.name} />
          <div className="pingflo-tour-card__copy">
            <h2 id="pingflo-tour-title">{title}</h2>
            <p aria-hidden={muted ? undefined : true}>{body}</p>
            <div className="sr-only" aria-live="polite">
              {liveMessage || `${agent.name}. ${title}. ${body}`}
            </div>
          </div>
        </div>
        <div className="pingflo-tour-card__meta">
          <span className="pingflo-tour-card__step">
            {stepIndex + 1}/{stepCount}
          </span>
          <button
            type="button"
            className="pingflo-tour-mute"
            aria-pressed={!muted}
            aria-label={muted ? t("tour_sound_on", "Sound on") : t("tour_sound_off", "Sound off")}
            title={muted ? t("tour_sound_on", "Sound on") : t("tour_sound_off", "Sound off")}
            onClick={() => {
              const next = !muted;
              setMuted(next);
              setTourTtsMuted(next);
            }}
          >
            {muted ? <VolumeX size={18} strokeWidth={2.2} aria-hidden /> : <Volume2 size={18} strokeWidth={2.2} aria-hidden />}
          </button>
        </div>
        <div className="pingflo-tour-card__actions">
          <button
            type="button"
            className="pingflo-tour-card__ghost"
            onClick={step?.choice === "technicalProfiling" ? onChoiceDefer : onSkip}
          >
            {step?.choice === "technicalProfiling"
              ? t("tour_skip", "Not now")
              : t("tour_skip", "Skip")}
          </button>
          {step?.doForMe && step?.choice !== "technicalProfiling" ? (
            <button type="button" className="pingflo-tour-card__secondary" onClick={onDoForMe}>
              {t("tour_do_for_me", "Do it for me")}
            </button>
          ) : null}
          <button
            type="button"
            className="pingflo-tour-card__primary"
            onClick={() =>
              step?.choice === "technicalProfiling"
                ? onChoiceAccept?.()
                : onNext?.({ from: "next" })
            }
          >
            {step?.choice === "technicalProfiling"
              ? t("tour_player_tech_start", "Start now")
              : stepIndex + 1 >= stepCount
                ? t("tour_done", "Done")
                : t("tour_next", "Next")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function TourWelcomeCard({ agentId = "pingflo", role, onStart, onSkip }) {
  const { t } = useTranslation();
  const host = AGENTS[agentId] || AGENTS.pingflo;
  const hostId = host.id || agentId;
  const welcomeBody =
    role === "parent"
      ? t("tour_welcome_body_parent", t("tour_welcome_body"))
      : t("tour_welcome_body");
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="pingflo-tour-welcome-scrim" role="presentation">
      <div className="pingflo-tour-welcome" role="dialog" aria-modal="true" aria-labelledby="pingflo-welcome-title">
        <div className="pingflo-tour-welcome__agents">
          {["pingflo", "pingwi", "pingpro", "pingmynd"].map((id) => (
            <AgentMark key={id} agentId={id} size={id === hostId ? 52 : 32} showGlow={id === hostId} />
          ))}
        </div>
        <h2 id="pingflo-welcome-title">{t("tour_welcome_title", { name: host.name })}</h2>
        <p>{welcomeBody}</p>
        <p>{t("tour_welcome_choice", "Finish the guided tour now, or continue later from the manual.")}</p>
        <div className="pingflo-tour-welcome__actions">
          <button type="button" className="pingflo-tour-card__ghost" onClick={onSkip}>
            {t("tour_continue_later", "Continue later")}
          </button>
          <button type="button" className="pingflo-tour-card__primary" onClick={onStart}>
            {t("tour_finalize_now", "Start guided tour")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function TourTechProfileCard({ onStart, onLater }) {
  const { t } = useTranslation();
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="pingflo-tour-welcome-scrim pingflo-tour-welcome-scrim--soft" role="presentation">
      <div
        className="pingflo-tour-welcome"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pingflo-tech-title"
      >
        <AgentMark agentId="pingflo" size={48} showGlow />
        <h2 id="pingflo-tech-title">{t("tour_player_tech_title")}</h2>
        <p>{t("tour_player_tech_body")}</p>
        <div className="pingflo-tour-welcome__actions">
          <button type="button" className="pingflo-tour-card__ghost" onClick={onLater}>
            {t("tour_player_tech_later", "Do it later")}
          </button>
          <button type="button" className="pingflo-tour-card__primary" onClick={onStart}>
            {t("tour_player_tech_start", "Start now")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function TourFirstJobCard({ title, body, onShow, onLater }) {
  const { t } = useTranslation();
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="pingflo-tour-welcome" role="dialog">
      <AgentMark agentId="pingflo" size={40} showGlow />
      <h2>{title}</h2>
      <p>{body}</p>
      <div className="pingflo-tour-welcome__actions">
        <button type="button" className="pingflo-tour-card__ghost" onClick={onLater}>
          {t("tour_later", "Later")}
        </button>
        <button type="button" className="pingflo-tour-card__primary" onClick={onShow}>
          {t("tour_show_me", "Show me")}
        </button>
      </div>
    </div>,
    document.body
  );
}
